// FLAGS: none
// Typed access to the REACT_APP_* values, merging the build-time `processEnv` define with the
// runtime `window.env` that react-inject-env writes. Everything reachable from here is served to the
// browser, so the REACT_APP_* set is public by construction — never put a secret in it.
declare global {
  interface Window {
    env?: Record<string, string>;
  }
}

interface Env {
  REACT_APP_GOOGLE_CLIENT_ID: string;
  REACT_APP_HOSTING_ENV: string;
  REACT_APP_API_SUBDOMAIN: string;
  /**
   * LOCAL-ONLY developer toggle — set it in .env.development.local, never in
   * an environment's variables.json or a deployed env.js. Only the exact
   * string "true" opts in to the in-app typed mock; everything else, including
   * the default, uses the real /api/v1/convene/* endpoints.
   *
   * Opt-in on purpose: as an opt-out flag, any deployment that forgot the key
   * served fabricated data with nothing on screen to indicate it. Because the
   * default is now safe, deployed variables.json files do not need this key at
   * all — which is what makes "never set it in a deployed file" enforceable
   * rather than merely stated.
   */
  REACT_APP_SPACES_USE_MOCK: string;
  /**
   * Origin of my.openup.com for the environment. Invite links are minted **by the API**
   * (`{MOU}/spaces/invite/{token}`) and this app only ever displays the URL it is handed, so the
   * one consumer is the local mock, which uses this to produce a link of the real shape instead of
   * one pointing at a route that does not exist. Do not build invitee URLs from it in app code:
   * the token half is server-side and the server owns the whole string.
   */
  REACT_APP_MOU_BASE_URL: string;
}

const defaults: Env = {
  REACT_APP_GOOGLE_CLIENT_ID: "",
  REACT_APP_HOSTING_ENV: "",
  REACT_APP_API_SUBDOMAIN: "",
  REACT_APP_SPACES_USE_MOCK: "false",
  REACT_APP_MOU_BASE_URL: "https://my.openup.com",
};

// Build-time values come from Vite `define` (processEnv); runtime values are
// injected by react-inject-env into /env.js (window.env) and take precedence.
const runtimeEnv: Record<string, string> =
  typeof window !== "undefined" && window.env ? window.env : {};

export const env: Env = {
  ...defaults,
  ...(typeof processEnv === "undefined" ? {} : processEnv),
  ...runtimeEnv,
};

export const isProduction = () => env.REACT_APP_HOSTING_ENV === "production";
